<script>
    import { preventDefault } from 'svelte/legacy';

    import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
    import { markerState } from "@app/store/markerStore.svelte.js";
    import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
    import { projectState } from "@app/store/projectStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
    import { fade } from "svelte/transition";
    import { Circle } from "svelte-loading-spinners";
    import * as m from '@app/paraglide/messages.js';

    //Get settings from local storage
    let defaultShotChangeSettings =
        JSON.parse(localStorage.getItem("cc-snap-to-shot-changes-settings")) ||
        {};

    let eventGapFrames = $state(defaultShotChangeSettings.eventGapFrames || 0),
        startTolerance = $state(defaultShotChangeSettings.startTolerance || 0.5),
        endTolerance = $state(defaultShotChangeSettings.endTolerance || 0.5),
        shotChangeOffset = $state(defaultShotChangeSettings.shotChangeOffset || 0),
        processing = $state(false);

    function syncToShotChanges() {
        try {
            if (markerState.lists[0].markers.length === 0) {
                throw new Error(
                    m.no_shot_change_markers(),
                );
            }

            processing = true;
            const offsetInSeconds = shotChangeOffset / projectState.frameRate;

            toast.push(m.snap_to_shot_changes_started(), {
                classes: ["toast-info"],
            });

            markerState.lists[0].markers.forEach((shotChange, markerIndex) => {
                try {
                    let closestStartEvent, closestEndEvent;
                    let startToleranceEvents = eventGroupState[
                        projectState.selected
                    ].events.filter((event) => {
                        return (
                            event.start >= shotChange.time - startTolerance &&
                            event.start <= shotChange.time + startTolerance
                        );
                    });

                    let endToleranceEvents = eventGroupState[
                        projectState.selected
                    ].events.filter((event) => {
                        return (
                            event.end >= shotChange.time - endTolerance &&
                            event.end <= shotChange.time + endTolerance
                        );
                    });

                    if (startToleranceEvents.length > 1) {
                        closestStartEvent = startToleranceEvents.reduce(
                            (prev, curr) => {
                                return Math.abs(curr.start - shotChange.time) <
                                    Math.abs(prev.start - shotChange.time)
                                    ? curr
                                    : prev;
                            },
                        );
                    } else if (startToleranceEvents.length === 1) {
                        closestStartEvent = startToleranceEvents[0];
                    }

                    if (endToleranceEvents.length > 1) {
                        closestEndEvent = endToleranceEvents.reduce(
                            (prev, curr) => {
                                return Math.abs(curr.end - shotChange.time) <
                                    Math.abs(prev.end - shotChange.time)
                                    ? curr
                                    : prev;
                            },
                        );
                    } else if (endToleranceEvents.length === 1) {
                        closestEndEvent = endToleranceEvents[0];
                    }

                    if (!closestEndEvent && !closestStartEvent) {
                        return;
                    }

                    if (
                        closestStartEvent &&
                        closestEndEvent &&
                        closestStartEvent.id === closestEndEvent.id
                    ) {
                        closestStartEvent.start =
                            shotChange.time + offsetInSeconds;
                    } else if (closestStartEvent && closestEndEvent) {
                        // Priority to the event starting on shot change
                        closestStartEvent.start =
                            shotChange.time + offsetInSeconds;
                        // Subtract frame gap from the end event to maintain gap
                        closestEndEvent.end = shotChange.time - offsetInSeconds - (eventGapFrames / projectState.frameRate);
                    } else if (closestStartEvent) {
                        closestStartEvent.start =
                            shotChange.time + offsetInSeconds;
                    } else if (closestEndEvent) {
                        closestEndEvent.end = shotChange.time - offsetInSeconds;
                    }

                    if (closestEndEvent) {
                        let closestEndEventIndex = eventGroupState[
                            projectState.selected
                        ].events.findIndex(
                            (event) => event.id === closestEndEvent.id,
                        );
                        eventGroupState[projectState.selected].events[
                            closestEndEventIndex
                        ] = closestEndEvent;
                        if (
                            closestEndEventIndex <
                                eventGroupState[projectState.selected].events
                                    .length -
                                    1 &&
                            eventGroupState[projectState.selected].events[
                                closestEndEventIndex + 1
                            ].start <= closestEndEvent.end
                        ) {
                            eventGroupState[projectState.selected].events[
                                closestEndEventIndex + 1
                            ].start =
                                closestEndEvent.end +
                                eventGapFrames / projectState.frameRate;
                        }
                    }

                    if (closestStartEvent) {
                        let closestStartEventIndex = eventGroupState[
                            projectState.selected
                        ].events.findIndex(
                            (event) => event.id === closestStartEvent.id,
                        );
                        eventGroupState[projectState.selected].events[
                            closestStartEventIndex
                        ] = closestStartEvent;
                        if (
                            closestStartEventIndex > 0 &&
                            eventGroupState[projectState.selected].events[
                                closestStartEventIndex - 1
                            ].end >= closestStartEvent.start
                        ) {
                            eventGroupState[projectState.selected].events[
                                closestStartEventIndex - 1
                            ].end = closestStartEvent.start;
                            eventGroupState[projectState.selected].events[
                                closestStartEventIndex
                            ].start += eventGapFrames / projectState.frameRate;
                        }
                    }
                } catch (err) {
                    console.info(
                        "Error processing shot change",
                        err.message,
                        markerIndex,
                        shotChange,
                    );
                    console.error(err);
                }
            });

            eventGroupState[projectState.selected] =
                eventGroupState[projectState.selected];

            insertHistoryAction({
                name: "snap to shot changes",
                eventGroup: projectState.selected,
                snapshots: [
                    {
                        store: "eventGroupState",
                        value: JSON.stringify(eventGroupState),
                    },
                ],
            });

            toast.push(m.snap_to_shot_changes_success(), {
                classes: ["toast-success"],
            });

            localStorage.setItem(
                "cc-snap-to-shot-changes-settings",
                JSON.stringify({
                    eventGapFrames,
                    startTolerance,
                    endTolerance,
                    shotChangeOffset,
                }),
            );
        } catch (err) {
            console.error(err, err.message);
            toast.push(
                m.snap_to_shot_changes_failed({ error: err.message }),
                { classes: ["toast-danger"] },
            );
        } finally {
            hideModal();
        }
    }
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'snapToShotChanges' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="snapToShotChangesTitle"
    aria-describedby="snapToShotChangesDescription"
    tabindex="-1"
    id="snapToShotChangesModal"
>
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="snapToShotChangesTitle">{m.snap_to_shot_changes()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.close_modal()}
                    id="closeSnapToShotChanges"
                    onclick={hideModal}
                ></button>
            </div>
            <div class="modal-body">
                <p id="snapToShotChangesDescription" class="visually-hidden">
                    {m.snap_to_shot_changes_description()}
                </p>
                <form onsubmit={preventDefault(syncToShotChanges)} aria-label={m.shot_change_settings()}>
                    <div class="row">
                        <div class="col-12 my-3">
                            <label class="form-label" for="eventGapFrames">{m.event_gap_frames()}</label>
                            <input
                                class="form-control"
                                type="number"
                                min="0"
                                step="1"
                                id="eventGapFrames"
                                aria-describedby="eventGapHelp"
                                bind:value={eventGapFrames}
                            />
                            <div id="eventGapHelp" class="form-text">{m.event_gap_frames_help()}</div>
                        </div>
                        <div class="col-12 my-3">
                            <label class="form-label" for="shotChangeOffset">{m.shot_change_offset_frames()}</label>
                            <input
                                class="form-control"
                                type="number"
                                min="0"
                                step="1"
                                id="shotChangeOffset"
                                aria-describedby="offsetHelp"
                                bind:value={shotChangeOffset}
                            />
                            <div id="offsetHelp" class="form-text">{m.shot_change_offset_help()}</div>
                        </div>
                        <div class="col-6 my-3">
                            <label class="form-label" for="startTolerance">{m.start_tolerance_seconds()}</label>
                            <input
                                class="form-control"
                                type="number"
                                min="0"
                                step="0.01"
                                id="startTolerance"
                                aria-describedby="startToleranceHelp"
                                bind:value={startTolerance}
                            />
                            <div id="startToleranceHelp" class="form-text">{m.start_tolerance_help()}</div>
                        </div>
                        <div class="col-6 my-3">
                            <label class="form-label" for="endTolerance">{m.end_tolerance_seconds()}</label>
                            <input
                                class="form-control"
                                type="number"
                                min="0"
                                step="0.01"
                                id="endTolerance"
                                aria-describedby="endToleranceHelp"
                                bind:value={endTolerance}
                            />
                            <div id="endToleranceHelp" class="form-text">{m.end_tolerance_help()}</div>
                        </div>
                    </div>
                    <button
                        class="btn btn-primary float-end"
                        type="button"
                        id="applySnapChanges"
                        onclick={syncToShotChanges}
                        disabled={processing}
                        aria-busy={processing}
                        >{#if processing}<Circle
                                size="10"
                                color="#1eb4b2"
                                unit="px"
                                duration="1s"
                            ></Circle>{/if} {m.apply()}</button
                    >
                </form>
            </div>
        </div>
    </div>
</div>
